import { Extension } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { Plugin, PluginKey, TextSelection, type EditorState, type Transaction } from "@tiptap/pm/state";
import { Decoration, DecorationSet, type EditorView } from "@tiptap/pm/view";
import { yUndoPluginKey } from "y-prosemirror";
import { searchPattern, type SearchOptions } from "@quill/shared";
import { touchedBlocks } from "./blocks";

export interface FindQuery extends SearchOptions {
  text: string;
}

interface Range {
  from: number;
  to: number;
}

interface FindState {
  pattern: RegExp | null;
  matches: DecorationSet;
  /** The match Next/Replace act on. */
  current: Range | null;
  /** Stopped counting at MAX_MATCHES. */
  capped: boolean;
}

const findKey = new PluginKey<FindState>("find");
/** More than this is not a useful search ("e"), and highlighting it all would be slow. */
const MAX_MATCHES = 5000;

type Meta = { query: FindQuery | null; from: number } | { current: Range | null } | { after: number };

const toPattern = (q: FindQuery) => searchPattern(q.text, q);

function scanBlock(node: PMNode, pos: number, pattern: RegExp, out: Decoration[], limit: number) {
  // Leaf nodes (hard breaks) count as one character, so text offsets are document offsets.
  const text = node.textBetween(0, node.content.size, undefined, " ");
  for (const m of text.matchAll(pattern)) {
    if (out.length >= limit || !m[0].length) return;
    const from = pos + 1 + m.index;
    out.push(Decoration.inline(from, from + m[0].length, { class: "find-match" }));
  }
}

function scanAll(doc: PMNode, pattern: RegExp): { matches: DecorationSet; capped: boolean } {
  const out: Decoration[] = [];
  doc.descendants((node, pos) => {
    if (out.length >= MAX_MATCHES) return false;
    if (node.isTextblock) scanBlock(node, pos, pattern, out, MAX_MATCHES);
    return !node.isTextblock;
  });
  return { matches: DecorationSet.create(doc, out), capped: out.length >= MAX_MATCHES };
}

const all = (s: FindState): Range[] => s.matches.find().map((d) => ({ from: d.from, to: d.to }));

/** The first match starting at or after `pos`, wrapping round to the first. */
function firstFrom(s: FindState, pos: number): Range | null {
  const list = all(s);
  return list.find((m) => m.from >= pos) ?? list[0] ?? null;
}

const EMPTY: FindState = { pattern: null, matches: DecorationSet.empty, current: null, capped: false };

function apply(tr: Transaction, prev: FindState, oldState: EditorState): FindState {
  const meta = tr.getMeta(findKey) as Meta | undefined;
  if (meta && "query" in meta) {
    const pattern = meta.query && toPattern(meta.query);
    if (!pattern) return EMPTY;
    const next = { ...EMPTY, pattern, ...scanAll(tr.doc, pattern) };
    return { ...next, current: firstFrom(next, meta.from) };
  }
  if (meta && "current" in meta) return { ...prev, current: meta.current };
  if (!prev.pattern || !tr.docChanged) return prev;

  let matches = prev.matches.map(tr.mapping, tr.doc);
  for (const pos of touchedBlocks(tr, oldState.selection.head)) {
    const node = tr.doc.nodeAt(pos)!;
    const out: Decoration[] = [];
    scanBlock(node, pos, prev.pattern, out, Infinity);
    matches = matches.remove(matches.find(pos, pos + node.nodeSize)).add(tr.doc, out);
  }
  const next = { ...prev, matches };
  // Stay where we were: on the same match if it survived, else the next one.
  const anchor = meta && "after" in meta ? meta.after : prev.current ? tr.mapping.map(prev.current.from, -1) : 0;
  return { ...next, current: firstFrom(next, anchor) };
}

/** Highlights search matches; the bar (FindBar) drives it through the commands below. */
export const Find = Extension.create({
  name: "find",
  addProseMirrorPlugins() {
    return [
      new Plugin<FindState>({
        key: findKey,
        state: { init: () => EMPTY, apply },
        props: { decorations: (state) => findKey.getState(state)!.matches },
      }),
      // The current match on its own layer, so moving between matches doesn't rebuild the rest.
      new Plugin({
        props: {
          decorations(state) {
            const cur = findKey.getState(state)!.current;
            return cur && DecorationSet.create(state.doc, [Decoration.inline(cur.from, cur.to, { class: "find-current" })]);
          },
        },
      }),
    ];
  },
});

export interface FindStatus {
  count: number;
  /** 1-based position of the current match, 0 if none. */
  index: number;
  capped: boolean;
  current: Range | null;
}

export function findStatus(state: EditorState): FindStatus {
  const s = findKey.getState(state);
  if (!s?.pattern) return { count: 0, index: 0, capped: false, current: null };
  const count = s.matches.find().length;
  const index = s.current ? s.matches.find(0, s.current.from).filter((d) => d.from < s.current!.from).length + 1 : 0;
  return { count, index, capped: s.capped, current: s.current };
}

/** Starts (or clears, with null) a search; the first match is the one at or after the caret. */
export function setQuery(view: EditorView, query: FindQuery | null) {
  view.dispatch(view.state.tr.setMeta(findKey, { query, from: view.state.selection.from } satisfies Meta));
}

export function step(view: EditorView, dir: 1 | -1) {
  const s = findKey.getState(view.state)!;
  const list = all(s);
  if (!list.length) return;
  const i = s.current ? list.findIndex((m) => m.from === s.current!.from) : -1;
  const next = list[(i + dir + list.length) % list.length]!;
  view.dispatch(view.state.tr.setMeta(findKey, { current: next } satisfies Meta));
}

/** Dispatches as its own undo step, not merged with typing or a replace just before. */
function dispatchAlone(view: EditorView, tr: Transaction) {
  const undo = (yUndoPluginKey.getState(view.state) as { undoManager?: { stopCapturing(): void } } | undefined)?.undoManager;
  undo?.stopCapturing();
  view.dispatch(tr);
  undo?.stopCapturing();
}

/** Makes the match with this number (from 0, in document order) current, if there is one. */
export function goTo(view: EditorView, index: number) {
  const s = findKey.getState(view.state);
  const match = s && all(s)[index];
  if (match) view.dispatch(view.state.tr.setMeta(findKey, { current: match } satisfies Meta));
}

const replaceRange = (tr: Transaction, { from, to }: Range, text: string) =>
  text ? tr.insertText(text, from, to) : tr.delete(from, to);

/** Replaces the current match and moves to the next one (never into the replacement itself). */
export function replaceCurrent(view: EditorView, text: string) {
  const cur = findKey.getState(view.state)?.current;
  if (!cur) return;
  const tr = replaceRange(view.state.tr, cur, text);
  dispatchAlone(view, tr.setMeta(findKey, { after: cur.from + text.length } satisfies Meta));
}

/** Replaces every match in one step (one undo). Returns how many were replaced. */
export function replaceAll(view: EditorView, text: string): number {
  const s = findKey.getState(view.state);
  if (!s?.pattern) return 0;
  // Every match, not just the highlighted ones (the highlights stop at MAX_MATCHES).
  const out: Decoration[] = [];
  view.state.doc.descendants((node, pos) => {
    if (node.isTextblock) scanBlock(node, pos, s.pattern!, out, Infinity);
    return !node.isTextblock;
  });
  if (!out.length) return 0;
  const tr = view.state.tr;
  for (const d of out.reverse()) replaceRange(tr, d, text);
  dispatchAlone(view, tr);
  return out.length;
}

/** Scrolls so the match sits in the upper part of the screen, clear of the bars at the bottom. */
export function revealMatch(view: EditorView, range: Range) {
  const vv = window.visualViewport;
  const height = vv?.height ?? window.innerHeight;
  const bottomBars = document.querySelector(".find-bar")?.getBoundingClientRect().height ?? 0;
  const status = document.querySelector(".status-line")?.getBoundingClientRect().height ?? 0;
  const { top, bottom } = view.coordsAtPos(range.from);
  const visibleTop = (vv?.offsetTop ?? 0) + 16;
  const visibleBottom = (vv?.offsetTop ?? 0) + height - bottomBars - status - 16;
  if (top >= visibleTop && bottom <= visibleBottom) return;
  window.scrollTo({ top: window.scrollY + top - (vv?.offsetTop ?? 0) - height / 3, behavior: "instant" });
}

/** Selects the current match in the editor, e.g. when the bar closes. */
export function selectMatch(view: EditorView, { from, to }: Range) {
  view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, from, to)));
}
